import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { getApplicationSettings } from 'src/modules/applications/slices';
import {
  FreeCreditsHistoryEntry,
  FreeCreditsHistorySort,
  FreeCreditsHistorySortColumn,
  FreeCreditsHistorySubscription,
  FreeCreditsHistoryType,
} from 'src/modules/companies/types';
import { ChangeCell, HistoryTable, HistoryTableContainer } from './FreeCreditsHistoryTable.styles';

const TYPE_LABELS: Record<FreeCreditsHistoryType, string> = {
  grant: 'Grant',
  deduction: 'Deduction',
  overage: 'Overage',
  opening_balance: 'Opening balance',
};

const formatChange = (change: number) =>
  change > 0 ? `+${change.toLocaleString()}` : change.toLocaleString();

const formatSubscription = (subscription: FreeCreditsHistorySubscription) => {
  if (subscription.startType === 'onboarding_complete') return subscription.plan.name;
  const start = dayjs(subscription.startDate.substring(0, 10)).format('MM/DD/YYYY');
  const end = dayjs(subscription.endDate.substring(0, 10)).format('MM/DD/YYYY');
  return `${subscription.plan.name}, ${start} – ${end}`;
};

type FreeCreditsHistoryTableProps = {
  entries: FreeCreditsHistoryEntry[];
  // The server searches dates in the company's zone, so they are shown in it too.
  timezone: string;
  sorting?: {
    value: FreeCreditsHistorySort;
    onChange: (column: FreeCreditsHistorySortColumn) => void;
  };
};

type SortableHeaderProps = Pick<FreeCreditsHistoryTableProps, 'sorting'> & {
  column: FreeCreditsHistorySortColumn;
  label: string;
};

const SortableHeader: FC<SortableHeaderProps> = ({ sorting, column, label }) => {
  if (!sorting) return <>{label}</>;
  const active = sorting.value.startsWith(`${column}_`);
  return (
    <TableSortLabel
      active={active}
      direction={active && sorting.value.endsWith('_asc') ? 'asc' : 'desc'}
      onClick={() => sorting.onChange(column)}
    >
      {label}
    </TableSortLabel>
  );
};

// Sized to their content, so Reason gets the rest of the row.
const NARROW_CELL_SX = { width: '1%', whiteSpace: 'nowrap' } as const;

export const FreeCreditsHistoryTable: FC<FreeCreditsHistoryTableProps> = ({
  entries,
  timezone,
  sorting,
}) => {
  const settings = useSelector(getApplicationSettings);

  return (
    <HistoryTableContainer>
      <HistoryTable size="small" headerBackground={settings?.primaryTableHeaderColor}>
        <TableHead>
          <TableRow>
            <TableCell sx={NARROW_CELL_SX}>
              <SortableHeader sorting={sorting} column="date_created" label="Date" />
            </TableCell>
            <TableCell sx={NARROW_CELL_SX}>
              <SortableHeader sorting={sorting} column="type" label="Type" />
            </TableCell>
            <TableCell sx={NARROW_CELL_SX} align="right">
              <SortableHeader sorting={sorting} column="change" label="Change" />
            </TableCell>
            <TableCell sx={NARROW_CELL_SX} align="right">
              <SortableHeader sorting={sorting} column="balance_after" label="Balance" />
            </TableCell>
            <TableCell>
              <SortableHeader sorting={sorting} column="reason" label="Reason" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(entry => (
            <TableRow key={entry.id}>
              <TableCell sx={NARROW_CELL_SX}>
                {moment(entry.date).tz(timezone).format('MM/DD/YYYY hh:mm A')}
              </TableCell>
              <TableCell sx={NARROW_CELL_SX}>{TYPE_LABELS[entry.type]}</TableCell>
              <ChangeCell align="right" sx={NARROW_CELL_SX} negative={entry.change < 0}>
                {formatChange(entry.change)}
              </ChangeCell>
              <TableCell sx={NARROW_CELL_SX} align="right">
                {entry.balanceAfter.toLocaleString()}
              </TableCell>
              <TableCell>
                {entry.reason}
                {entry.subscription && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Subscription: {formatSubscription(entry.subscription)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </HistoryTable>
    </HistoryTableContainer>
  );
};
